// src/pages/workflow/ICPReview.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * Component for reviewing and validating ICP
 * @param {Object} props
 * @param {string} props.sessionId - Session ID
 * @param {string} props.icpString - ICP JSON string
 * @param {Function} props.onApprove - Callback when ICP is approved
 * @param {Function} props.onReject - Callback when ICP is rejected
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 */
function ICPReview({ icpString, onApprove, onReject, loading, error }) {
  const [feedback, setFeedback] = useState('');
  const [parsedICP, setParsedICP] = useState(null);

  useEffect(() => {
    if (icpString) {
      try {
        const parsed = JSON.parse(icpString);
        setParsedICP(parsed);
      } catch (e) {
        console.error('Failed to parse ICP:', e);
        setParsedICP(null);
      }
    }
  }, [icpString]);

  const handleApprove = async () => {
    try {
      await onApprove();
    } catch (err) {
      console.error('Failed to approve:', err);
    }
  };

  const handleReject = async () => {
    try {
      await onReject(feedback || undefined);
    } catch (err) {
      console.error('Failed to reject:', err);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Review ICP
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
        Review the generated Ideal Customer Profile below. You can approve to continue or reject with feedback for calibration.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {loading && !parsedICP && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading ICP...</Typography>
        </Box>
      )}

      {parsedICP ? (
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <CardContent>
            {parsedICP.attributes && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Attributes
                </Typography>
                {parsedICP.attributes.title && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline' }}>
                      Titles:{' '}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {parsedICP.attributes.title.map((title, idx) => (
                        <Chip key={idx} label={title} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {parsedICP.attributes.company && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline' }}>
                      Companies:{' '}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {parsedICP.attributes.company.map((company, idx) => (
                        <Chip key={idx} label={company} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {parsedICP.attributes.location && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline' }}>
                      Locations:{' '}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {parsedICP.attributes.location.map((location, idx) => (
                        <Chip key={idx} label={location} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {parsedICP.attributes.skills && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline' }}>
                      Skills:{' '}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {parsedICP.attributes.skills.map((skill, idx) => (
                        <Chip key={idx} label={skill} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {parsedICP.icpStructure && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  ICP Structure
                </Typography>
                {parsedICP.icpStructure.whatPersonWillDo && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      What this person will do:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {parsedICP.icpStructure.whatPersonWillDo}
                    </Typography>
                  </Box>
                )}
                {parsedICP.icpStructure.criticalSkills && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Critical Skills:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {parsedICP.icpStructure.criticalSkills.map((skill, idx) => (
                        <Chip key={idx} label={skill} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
                {parsedICP.icpStructure.similarRoles && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Similar Roles:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {parsedICP.icpStructure.similarRoles.map((role, idx) => (
                        <Chip key={idx} label={role} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {parsedICP.icpStructure.companies && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Companies:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {parsedICP.icpStructure.companies.map((company, idx) => (
                        <Chip key={idx} label={company} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {parsedICP.icpStructure.possibleContext && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Context:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {parsedICP.icpStructure.possibleContext}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      ) : icpString ? (
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              ICP (Raw JSON)
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 400
              }}
            >
              {icpString}
            </Box>
          </CardContent>
        </Card>
      ) : null}

      <Card sx={{ boxShadow: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Feedback (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Provide feedback for calibration (e.g., 'Prefer payments domain, Bay Area only')"
            disabled={loading}
            sx={{ mb: 2 }}
          />
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleReject}
          disabled={loading}
          startIcon={<CancelIcon />}
          sx={{
            borderColor: '#f44336',
            color: '#f44336',
            '&:hover': { borderColor: '#d32f2f', bgcolor: '#ffebee' }
          }}
        >
          {loading ? 'Processing...' : 'Reject & Calibrate'}
        </Button>
        <Button
          variant="contained"
          onClick={handleApprove}
          disabled={loading}
          startIcon={<CheckCircleIcon />}
          sx={{
            bgcolor: '#1BE885',
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#20ec84' },
            '&:disabled': { bgcolor: '#ccc' }
          }}
        >
          {loading ? 'Processing...' : 'Approve & Continue'}
        </Button>
      </Box>
    </Box>
  );
}

export default ICPReview;
